import { memo, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { JsonEditor } from 'json-edit-react';
import type { RemirrorEventListener } from 'remirror';
import { css } from '@emotion/css';
import {
  CheckCircleOutline,
  Close as CloseIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import type { CategoryGroup } from '@trinity/chart-theming';
import { ChartThemePicker, TRINITY_THEME } from '@trinity/chart-theming';
import { type Remirror as RemirrorTypes } from '@trinity/chat';
import type {
  ChartWidgetConfig,
  ChartStaticRow,
  StaticSeries,
  DashboardChartType,
  MetricCardWidgetConfig,
  TextWidgetConfig,
  CodeWidgetConfig,
  RepDashboardWidgetConfig,
  WidgetDataSource,
  WidgetSpecificConfig,
  KpiItem,
  WidgetConfig,
} from '@trinity/chat/types/dashboard-config';
import { defaultColor } from '../../ColorSelect';
import { RepDashboardWidgetDataSchema } from '@trinity/chat/types/dashboard-data';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import {
  useDashboardBuilder,
  useDashboardBuilderEditing,
  useDashboardConfig,
  useDashboardDataContext,
  useWidgetRegistry,
} from '@trinity/chat-components/containers/Dashboard/context';
import type { BasicMarkupEditorExtensions } from '../../Remirror/BasicMarkupEditor';
import { BasicMarkupEditor } from '../../Remirror/BasicMarkupEditor';
import { useDashboardThemeCategories } from '../../../hooks/useDashboardThemeCategories';
import { CodeEditorModal } from './CodeEditorModal';
import { AgentIntegrationInfoDialog, type FetchState } from './AgentIntegrationInfoDialog';

// ─── Constants ────────────────────────────────────────────────────────────────

const CHART_TYPES: { value: DashboardChartType; label: string }[] = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'stacked-bar', label: 'Stacked Bar' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'multiple-line', label: 'Multiple Line' },
  { value: 'multiple-clustered-bar', label: 'Clustered Bar' },
  { value: 'multiple-stacked-bar', label: 'Multiple Stacked Bar' },
  { value: 'data-table', label: 'Data Table' },
  { value: 'kpi-widget', label: 'KPI Widget' },
  { value: 'progress-bar-widget', label: 'Progress Bar' },
  { value: 'semi-circle-progress-widget', label: 'Semi Circle Progress' },
];

const WIDGET_TYPE_LABELS: Record<string, string> = {
  chart: 'Chart',
  text: 'Text',
  metric_card: 'Metric Card',
  code: 'Code',
  rep_dashboard: 'Rep Dashboard',
};

/** Minimal valid Remirror/ProseMirror document used when no content has been authored yet */
const EMPTY_DOC_NODE = {
  type: 'doc',
  content: [{ type: 'paragraph', content: [] }],
} as unknown as RemirrorTypes.RootNode;

const THEMED_SELECT_MENU_PROPS = (theme: Theme) => ({
  PaperProps: {
    sx: {
      backgroundColor: theme.palette.background.paper,
      backgroundImage: 'none',
    },
  },
});

const toolbarNoBorder = css`
  border: none !important;
  padding: 0 !important;
`;

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Working data-source type used inside the sidebar's live/draft state.
 * The `data` field is nullable so partially-filled forms produce a valid shape.
 * Only converted to the strict `WidgetDataSource` (with required `data`) at commit time.
 */
type LiveDataSource =
  | { type: 'static'; data: CanonicalWidgetData | null }
  | { type: 'agent'; agentId: number; extensionPath: string; dataModel?: CanonicalWidgetData };

// ─── Canonical-data builder helpers ──────────────────────────────────────────

/** Builds a `CanonicalWidgetData` for a chart widget from raw form values. */
function buildChartCanonical(
  chartType: DashboardChartType,
  title: string,
  xAxisLabel: string,
  yAxisLabel: string,
  rows: ChartStaticRow[],
  series: StaticSeries[]
): CanonicalWidgetData {
  switch (chartType) {
    case 'pie':
      return {
        type: 'chart',
        data: {
          type: 'pie',
          chart: {
            title,
            xAxisTitle: xAxisLabel,
            yAxisTitle: yAxisLabel,
            data: rows.map((r) => ({
              id: String(r.category),
              label: String(r.category),
              value: Number(r.value) || 0,
            })),
          },
        },
      };
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
      return {
        type: 'chart',
        data: {
          type: chartType,
          chart: {
            title,
            xAxisTitle: xAxisLabel,
            yAxisTitle: yAxisLabel,
            indexBy: 'x',
            data: series.map((s) => ({
              index: s.name || 'Series',
              data: s.rows.map((r) => ({ x: String(r.category), y: Number(r.value) || 0 })),
            })),
          },
        },
      };
    case 'multiple-line':
      return {
        type: 'chart',
        data: {
          type: 'multiple-line' as const,
          chart: {
            title,
            xAxisTitle: xAxisLabel,
            yAxisTitle: yAxisLabel,
            data: series.map((s) => ({
              id: s.name || 'Series',
              data: s.rows.map((r) => ({ x: String(r.category), y: Number(r.value) || 0 })),
            })),
          },
        },
      };
    case 'kpi-widget':
    case 'progress-bar-widget':
    case 'semi-circle-progress-widget':
      return {
        type: 'chart',
        data: {
          type: chartType,
          title,
          backgroundColor: '#ffffff',
          values: rows.map((r) => String(r.value)),
          labels: rows.map((r) => String(r.category)),
        },
      };
    case 'data-table':
      // data-table uses buildDataTableCanonical instead — this is a fallback
      return {
        type: 'chart',
        data: {
          type: 'data-table',
          title,
          headerColor: defaultColor,
          data: {
            columnNames: [['Category'], ['Value']],
            rowNames: rows.map((_, i) => [String(i + 1)]),
            records: rows.map((r) => [String(r.category), String(r.value)]),
          },
        },
      };
    case 'line':
      return {
        type: 'chart',
        data: {
          type: 'multiple-line' as const,
          chart: {
            title,
            xAxisTitle: xAxisLabel,
            yAxisTitle: yAxisLabel,
            data: [
              {
                id: 'series',
                data: rows.map((r) => ({ x: String(r.category), y: Number(r.value) || 0 })),
              },
            ],
          },
        },
      };
    default:
      return {
        type: 'chart',
        data: {
          type: chartType,
          chart: {
            title,
            xAxisTitle: xAxisLabel,
            yAxisTitle: yAxisLabel,
            indexBy: 'x',
            data: rows.map((r) => ({ x: String(r.category), y: Number(r.value) || 0 })),
          },
        },
      };
  }
}

/** Builds a `CanonicalWidgetData` for a data-table widget from dynamic columns/rows. */
function buildDataTableCanonical(
  title: string,
  columns: string[],
  tableRows: { id: string; cells: string[] }[]
): CanonicalWidgetData {
  return {
    type: 'chart',
    data: {
      type: 'data-table',
      title,
      headerColor: defaultColor,
      data: {
        columnNames: columns.map((c) => [c]),
        rowNames: tableRows.map((r) => [r.id]),
        records: tableRows.map((r) => columns.map((_, ci) => r.cells[ci] ?? '')),
      },
    },
  };
}

/** Extracts data-table columns and rows from a widget's `dataSource`. */
function dataTableFromDataSource(ds: WidgetDataSource | undefined): {
  columns: string[];
  rows: { id: string; cells: string[] }[];
} {
  if (ds?.type !== 'static' || !ds.data || ds.data.type !== 'chart') {
    return { columns: ['Category', 'Value'], rows: [] };
  }
  const chartData = ds.data.data;
  if (chartData.type !== 'data-table' || !('data' in chartData)) {
    return { columns: ['Category', 'Value'], rows: [] };
  }
  const tbl = chartData.data as { columnNames: string[][]; rowNames: string[][]; records: string[][] };
  const columns = tbl.columnNames.map((cn) => cn[0] ?? '');
  const rows = tbl.records.map((rec, i) => ({
    id: tbl.rowNames[i]?.[0] ?? String(i + 1),
    cells: rec.map((c) => c ?? ''),
  }));
  return { columns, rows };
}

/**
 * Extracts editable row/series data from a widget's `dataSource` for chart forms.
 * Returns empty arrays when the source doesn't carry chart data yet.
 */
function chartRowsFromDataSource(
  ds: WidgetDataSource | undefined,
  chartType: DashboardChartType
): { staticRows: ChartStaticRow[]; staticSeries: StaticSeries[] } {
  if (ds?.type !== 'static' || !ds.data || ds.data.type !== 'chart') {
    return { staticRows: [], staticSeries: [] };
  }
  const chartData = ds.data.data;
  if (
    chartType === 'multiple-clustered-bar' ||
    chartType === 'multiple-stacked-bar' ||
    chartType === 'multiple-line'
  ) {
    if (
      chartData.type === 'multiple-clustered-bar' ||
      chartData.type === 'multiple-stacked-bar' ||
      chartData.type === 'multiple-line'
    ) {
      return {
        staticRows: [],
        staticSeries: (
          chartData.chart.data as Array<{ index: string; data: Array<{ x: unknown; y: unknown }> }>
        ).map((s) => ({
          name: s.index,
          rows: s.data.map((r) => ({ category: String(r.x), value: String(r.y) })),
        })),
      };
    }
  } else if (
    chartType === 'kpi-widget' ||
    chartType === 'progress-bar-widget' ||
    chartType === 'semi-circle-progress-widget'
  ) {
    if (
      chartData.type === 'kpi-widget' ||
      chartData.type === 'progress-bar-widget' ||
      chartData.type === 'semi-circle-progress-widget'
    ) {
      return {
        staticRows: (chartData.values as string[]).map((v, i) => ({
          category: (chartData.labels as string[])[i] ?? '',
          value: v,
        })),
        staticSeries: [],
      };
    }
  } else if ('chart' in chartData) {
    const data = (chartData.chart as { data: Array<Record<string, unknown>> }).data;
    return {
      staticRows: data.map((r) => ({
        category: String(r.label ?? r.x ?? ''),
        value: String(r.value ?? r.y ?? ''),
      })),
      staticSeries: [],
    };
  }
  return { staticRows: [], staticSeries: [] };
}

/** Extracts KPI items from a metric_card widget's `dataSource`. */
function metricKpisFromDataSource(ds: WidgetDataSource | undefined): KpiItem[] {
  if (ds?.type !== 'static' || !ds.data || ds.data.type !== 'metric_card') return [];
  const { values, labels } = ds.data.data;
  return (values as string[]).map((v, i) => ({ value: v, label: (labels as string[])[i] ?? '', unit: '' }));
}

/** Written synchronously by the form's commitRef; parent reads it immediately after calling commit */
type CommittedValues = {
  config: Partial<WidgetSpecificConfig>;
  dataSource: WidgetDataSource;
};

// ─── useAgentState ────────────────────────────────────────────────────────────

function useAgentState(widgetId: string, initialAgentId: number, initialExtensionPath: string) {
  const { fetchAgentPreview, agents } = useDashboardDataContext();
  const [agentId, setAgentId] = useState(initialAgentId);
  const [extensionPath, setExtensionPath] = useState(initialExtensionPath);
  const [agentIdError, setAgentIdError] = useState('');
  const [extensionPathError, setExtensionPathError] = useState('');
  const [fetchState, setFetchState] = useState<FetchState>('idle');
  const [fetchError, setFetchError] = useState('');

  const validateAgentId = useCallback((v: number): boolean => {
    if (!v || v <= 0) {
      setAgentIdError('Select an agent.');
      return false;
    }
    setAgentIdError('');
    return true;
  }, []);

  const validateExtensionPath = useCallback((v: string): boolean => {
    if (!v.trim()) {
      setExtensionPathError('Enter an extension path (e.g. /api/report).');
      return false;
    }
    setExtensionPathError('');
    return true;
  }, []);

  /** Callback invoked with the inner data when a fetch succeeds (e.g. to populate /data.js) */
  const onDataFetchedRef = useRef<((data: Record<string, unknown>) => void) | null>(null);

  // Monotonic counter to discard stale responses from rapid fetch clicks.
  const fetchSeqRef = useRef(0);

  const handleFetchPreview = useCallback(async () => {
    const idOk = validateAgentId(agentId);
    const pathOk = validateExtensionPath(extensionPath);
    if (!idOk || !pathOk) return;
    setFetchState('loading');
    setFetchError('');
    const seq = ++fetchSeqRef.current;
    const result = await fetchAgentPreview(agentId, extensionPath, widgetId);
    // Discard if a newer fetch was initiated while this one was in-flight
    if (seq !== fetchSeqRef.current) return;
    if (result) {
      setFetchState('success');
      // Extract inner data and notify listener (code widget modal uses this to populate /data.js)
      if (result.type === 'code' && onDataFetchedRef.current) {
        onDataFetchedRef.current(result.data);
      }
    } else {
      setFetchState('error');
      setFetchError('Failed to fetch data from the agent.');
    }
  }, [agentId, extensionPath, fetchAgentPreview, validateAgentId, validateExtensionPath, widgetId]);

  return {
    agentId,
    setAgentId,
    agentIdError,
    extensionPath,
    setExtensionPath,
    extensionPathError,
    fetchState,
    fetchError,
    validateAgentId,
    validateExtensionPath,
    handleFetchPreview,
    onDataFetchedRef,
    agents,
  };
}

// ─── AgentDataSection ─────────────────────────────────────────────────────────

interface AgentDataSectionProps {
  agentId: number;
  setAgentId: (v: number) => void;
  agentIdError: string;
  extensionPath: string;
  setExtensionPath: (v: string) => void;
  extensionPathError: string;
  fetchState: FetchState;
  fetchError: string;
  validateAgentId: (v: number) => boolean;
  validateExtensionPath: (v: string) => boolean;
  handleFetchPreview: () => void;
  agentDataExample: Record<string, unknown> | undefined;
  agents: { id: number; name: string }[];
}

function AgentDataSection({
  agentId,
  setAgentId,
  agentIdError,
  extensionPath,
  setExtensionPath,
  extensionPathError,
  fetchState,
  fetchError,
  validateAgentId,
  validateExtensionPath,
  handleFetchPreview,
  agentDataExample,
  agents,
}: AgentDataSectionProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setTooltipOpen(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleDownload = useCallback(() => {
    if (!agentDataExample) return;
    const blob = new Blob([JSON.stringify(agentDataExample, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'expected-response.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [agentDataExample]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <AgentIntegrationInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
      <FormControl size="small" fullWidth error={!!agentIdError}>
        <InputLabel id="agent-select-label">Agent</InputLabel>
        <Select
          labelId="agent-select-label"
          label="Agent"
          value={agentId || ''}
          onChange={(e) => {
            const v = Number(e.target.value);
            setAgentId(v);
            if (agentIdError) validateAgentId(v);
          }}
          onBlur={() => validateAgentId(agentId)}
        >
          {agents.length === 0 && (
            <MenuItem disabled value="">
              <em>No HTTP agents available</em>
            </MenuItem>
          )}
          {agents.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
        {agentIdError && <FormHelperText>{agentIdError}</FormHelperText>}
      </FormControl>
      <TextField
        label="Extension Path"
        size="small"
        fullWidth
        placeholder="/api/report"
        value={extensionPath}
        error={!!extensionPathError}
        helperText={extensionPathError}
        onChange={(e) => {
          setExtensionPath(e.target.value);
          if (extensionPathError) validateExtensionPath(e.target.value);
        }}
        onBlur={(e) => validateExtensionPath(e.target.value)}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleFetchPreview}
          disabled={fetchState === 'loading'}
          startIcon={fetchState === 'loading' ? <CircularProgress size={14} /> : undefined}
        >
          {fetchState === 'loading' ? 'Fetching…' : 'Fetch Preview'}
        </Button>
        {fetchState === 'success' && (
          <Tooltip title="Preview loaded successfully">
            <CheckCircleOutline fontSize="small" color="success" />
          </Tooltip>
        )}
        <Tooltip
          title="Click for agent integration guide"
          open={tooltipOpen}
          onClose={() => setTooltipOpen(false)}
          onOpen={() => setTooltipOpen(true)}
          arrow
        >
          <IconButton
            size="small"
            onClick={() => setInfoOpen(true)}
            sx={{ ml: 'auto', backgroundColor: '#e5f5fc', '&:hover': { backgroundColor: '#d0edf8' } }}
          >
            <InfoIcon fontSize="small" sx={{ color: '#28aae1' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {fetchState === 'error' && (
        <Typography variant="caption" color="error">
          {fetchError}
        </Typography>
      )}

      {agentDataExample && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 1,
              }}
            >
              <Typography variant="caption">Expected response format</Typography>
              <Tooltip title="Download as JSON">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  sx={{ p: 0.25 }}
                >
                  <DownloadIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <Box
              component="pre"
              sx={{
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                m: 0,
                overflow: 'auto',
                maxHeight: 240,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {JSON.stringify(agentDataExample, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

// ─── FormProps ────────────────────────────────────────────────────────────────

interface FormProps {
  widget: WidgetConfig;
  onUpdate: (partial: Partial<WidgetConfig['config']>) => void;
  onUpdateDataSource: (ds: WidgetDataSource) => void;
  /**
   * Parent calls this to commit form state.
   * Returns a validation error string, or null on success.
   * Also writes the committed values to `committedRef` synchronously.
   */
  commitRef: React.MutableRefObject<(() => string | null) | null>;
  /** Read by parent after commitRef returns null — contains the final config + dataSource */
  committedRef: React.MutableRefObject<CommittedValues | null>;
  /** Which tab is active: 0 = Info, 1 = Data */
  activeTab: number;
  /** Resolved panel name for chart forms (avoids re-computing tab index arithmetic in each form) */
  activePanelName: 'info' | 'data';
  /** Called by the form whenever local state changes (for live preview) */
  onLiveUpdate: (config: Partial<WidgetSpecificConfig>, dataSource: LiveDataSource) => void;
}

// ─── WidgetConfigForm ─────────────────────────────────────────────────────────

function WidgetConfigForm(props: FormProps) {
  switch (props.widget.type) {
    case 'chart':
      return <ChartConfigForm {...props} />;
    case 'text':
      return <TextConfigForm {...props} />;
    case 'metric_card':
      return <MetricConfigForm {...props} />;
    case 'code':
      return <CodeConfigForm {...props} />;
    case 'rep_dashboard':
      return <RepDashboardConfigForm {...props} />;
    default:
      return (
        <Typography variant="body2" color="text.secondary">
          No config for this widget type.
        </Typography>
      );
  }
}

// ─── ChartConfigForm ──────────────────────────────────────────────────────────

function ChartConfigForm({
  widget,
  onUpdate,
  onUpdateDataSource,
  commitRef,
  committedRef,
  activeTab: _activeTab,
  activePanelName,
  onLiveUpdate,
}: FormProps) {
  const { registry } = useWidgetRegistry();
  const entry = registry.get(widget.type);
  const cfg = widget.config as ChartWidgetConfig;
  const ds = widget.dataSource;

  const [title, setTitle] = useState(cfg.title ?? '');
  const [chartType, setChartType] = useState<DashboardChartType>(cfg.chartType ?? 'bar');
  const [xAxisLabel, setXAxisLabel] = useState(cfg.xAxisLabel ?? '');
  const [yAxisLabel, setYAxisLabel] = useState(cfg.yAxisLabel ?? '');
  const initRows = chartRowsFromDataSource(ds, cfg.chartType ?? 'bar');
  const [staticRows, setStaticRows] = useState<ChartStaticRow[]>(initRows.staticRows);
  const [staticSeries, setStaticSeries] = useState<StaticSeries[]>(initRows.staticSeries);
  const initTable = dataTableFromDataSource(ds);
  const [tableColumns, setTableColumns] = useState<string[]>(initTable.columns);
  const [tableRowData, setTableRowData] = useState<{ id: string; cells: string[] }[]>(initTable.rows);
  const [sourceType, setSourceType] = useState<'static' | 'agent'>(ds?.type === 'agent' ? 'agent' : 'static');
  const [paletteColors, setPaletteColors] = useState<string[]>(cfg.colors ?? [...TRINITY_THEME.colors]);
  const [chartThemeId, setChartThemeId] = useState<number | null>(cfg.chartThemeId ?? TRINITY_THEME.id);
  const agentAgent = useAgentState(
    widget.id,
    ds?.type === 'agent' ? ds.agentId : 0,
    ds?.type === 'agent' ? ds.extensionPath : ''
  );

  // Theme categories shaped for ChartThemePicker — must be declared before emitLive uses it
  const { createTheme } = useDashboardBuilderEditing();
  const categories = useDashboardThemeCategories(chartThemeId);

  // Emit live updates whenever relevant state changes — declared before theme handlers so they can reference it
  const emitLive = useCallback(
    (
      overrides: Partial<{
        title: string;
        chartType: DashboardChartType;
        xAxisLabel: string;
        yAxisLabel: string;
        staticRows: ChartStaticRow[];
        staticSeries: StaticSeries[];
        sourceType: 'static' | 'agent';
        paletteColors: string[];
        chartThemeId: number | null;
        tableColumns: string[];
        tableRowData: { id: string; cells: string[] }[];
      }> = {}
    ) => {
      const t = overrides.title ?? title;
      const ct = overrides.chartType ?? chartType;
      const xl = overrides.xAxisLabel ?? xAxisLabel;
      const yl = overrides.yAxisLabel ?? yAxisLabel;
      const rows = overrides.staticRows ?? staticRows;
      const ss = overrides.staticSeries ?? staticSeries;
      const st = overrides.sourceType ?? sourceType;
      const pc = overrides.paletteColors ?? paletteColors;
      const tid = overrides.chartThemeId !== undefined ? overrides.chartThemeId : chartThemeId;
      const tc = overrides.tableColumns ?? tableColumns;
      const tr = overrides.tableRowData ?? tableRowData;
      const liveConfig: Partial<ChartWidgetConfig> = {
        title: t,
        chartType: ct,
        xAxisLabel: xl || undefined,
        yAxisLabel: yl || undefined,
        chartThemeId: tid ?? undefined,
        colors: pc.length > 0 ? pc : undefined,
      };
      const liveDs: LiveDataSource =
        st === 'agent'
          ? { type: 'agent', agentId: agentAgent.agentId, extensionPath: agentAgent.extensionPath }
          : {
              type: 'static',
              data:
                ct === 'data-table'
                  ? buildDataTableCanonical(t, tc, tr)
                  : buildChartCanonical(ct, t, xl, yl, rows, ss),
            };
      onLiveUpdate(liveConfig, liveDs);
    },
    [
      title,
      chartType,
      xAxisLabel,
      yAxisLabel,
      staticRows,
      staticSeries,
      sourceType,
      paletteColors,
      chartThemeId,
      tableColumns,
      tableRowData,
      agentAgent.agentId,
      agentAgent.extensionPath,
      onLiveUpdate,
    ]
  );

  const onThemeSelect = useCallback(
    (themeId: number | null) => {
      setChartThemeId(themeId);
      const resolvedColors =
        themeId !== null
          ? (categories.flatMap((g) => g.items).find((t) => t.id === themeId)?.colors ?? [])
          : [];
      setPaletteColors(resolvedColors);
      emitLive({ paletteColors: resolvedColors, chartThemeId: themeId });
    },
    [categories, emitLive]
  );

  const onCreateTheme = useCallback(
    (name: string, colors: string[], _categoryId: number) => {
      const newId = createTheme(name, colors);
      setChartThemeId(newId);
      setPaletteColors(colors);
      emitLive({ paletteColors: colors, chartThemeId: newId });
    },
    [createTheme, emitLive]
  );

  const agentDataExample = entry?.getAgentDataExample?.({ ...cfg, chartType });

  // Assign commit function each render — always captures latest local state
  commitRef.current = () => {
    // Validation
    if (sourceType === 'static') {
      if (chartType === 'data-table') {
        if (tableColumns.length === 0) return 'Add at least one column before saving.';
        if (tableColumns.some((c) => c.trim() === '')) return 'All column names must be filled in.';
      } else {
        const isMultiSeries =
          chartType === 'multiple-clustered-bar' ||
          chartType === 'multiple-stacked-bar' ||
          chartType === 'multiple-line';
        if (isMultiSeries) {
          if (
            staticSeries.length === 0 ||
            staticSeries.every((s) => s.rows.every((r) => String(r.category).trim() === ''))
          ) {
            return 'Add at least one series with data before saving.';
          }
        } else {
          const filledRows = staticRows.filter((r) => String(r.category).trim() !== '');
          if (
            filledRows.length === 0 &&
            (chartType === 'bar' || chartType === 'stacked-bar' || chartType === 'pie')
          ) {
            return 'Add at least one data row with a category before saving.';
          }
        }
      }
    } else if (sourceType === 'agent') {
      if (!agentAgent.agentId || agentAgent.agentId <= 0) return 'Enter a valid Agent ID.';
      if (!agentAgent.extensionPath.trim()) return 'Enter an extension path.';
      if (!agentDataExample) return 'Widget data model is not configured.';
    }

    const newConfig: Partial<ChartWidgetConfig> = {
      title,
      chartType,
      chartThemeId: chartThemeId ?? undefined,
      xAxisLabel: xAxisLabel || undefined,
      yAxisLabel: yAxisLabel || undefined,
      colors: paletteColors.length > 0 ? paletteColors : undefined,
    };
    const model = agentDataExample;
    const staticData =
      chartType === 'data-table'
        ? buildDataTableCanonical(title, tableColumns, tableRowData)
        : buildChartCanonical(chartType, title, xAxisLabel, yAxisLabel, staticRows, staticSeries);
    const newDs: WidgetDataSource =
      sourceType === 'agent' && model
        ? {
            type: 'agent',
            agentId: agentAgent.agentId,
            extensionPath: agentAgent.extensionPath,
            dataModel: model,
          }
        : { type: 'static', data: staticData };

    // Write output synchronously before calling state setters
    committedRef.current = { config: newConfig, dataSource: newDs };
    onUpdate(newConfig);
    onUpdateDataSource(newDs);
    return null;
  };

  const addRow = () => {
    const next = [...staticRows, { category: '', value: '' }];
    setStaticRows(next);
    emitLive({ staticRows: next });
  };
  const updateRow = (i: number, field: keyof ChartStaticRow, val: string) => {
    const next = staticRows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r));
    setStaticRows(next);
    emitLive({ staticRows: next });
  };
  const removeRow = (i: number) => {
    const next = staticRows.filter((_, idx) => idx !== i);
    setStaticRows(next);
    emitLive({ staticRows: next });
  };

  // Multi-series management (multiple-clustered-bar / multiple-stacked-bar)
  const addSeries = () => {
    const next = [...staticSeries, { name: `Series ${staticSeries.length + 1}`, rows: [] }];
    setStaticSeries(next);
    emitLive({ staticSeries: next });
  };
  const updateSeriesName = (si: number, name: string) => {
    const next = staticSeries.map((s, idx) => (idx === si ? { ...s, name } : s));
    setStaticSeries(next);
    emitLive({ staticSeries: next });
  };
  const removeSeries = (si: number) => {
    const next = staticSeries.filter((_, idx) => idx !== si);
    setStaticSeries(next);
    emitLive({ staticSeries: next });
  };
  const addSeriesRow = (si: number) => {
    const next = staticSeries.map((s, idx) =>
      idx === si ? { ...s, rows: [...s.rows, { category: '', value: '' }] } : s
    );
    setStaticSeries(next);
    emitLive({ staticSeries: next });
  };
  const updateSeriesRow = (si: number, ri: number, field: keyof ChartStaticRow, val: string) => {
    const next = staticSeries.map((s, sIdx) =>
      sIdx === si ? { ...s, rows: s.rows.map((r, rIdx) => (rIdx === ri ? { ...r, [field]: val } : r)) } : s
    );
    setStaticSeries(next);
    emitLive({ staticSeries: next });
  };
  const removeSeriesRow = (si: number, ri: number) => {
    const next = staticSeries.map((s, sIdx) =>
      sIdx === si ? { ...s, rows: s.rows.filter((_, rIdx) => rIdx !== ri) } : s
    );
    setStaticSeries(next);
    emitLive({ staticSeries: next });
  };

  // Data-table column/row management
  const addTableColumn = () => {
    const nextCols = [...tableColumns, `Column ${tableColumns.length + 1}`];
    const nextRows = tableRowData.map((r) => ({ ...r, cells: [...r.cells, ''] }));
    setTableColumns(nextCols);
    setTableRowData(nextRows);
    emitLive({ tableColumns: nextCols, tableRowData: nextRows });
  };
  const updateTableColumnName = (ci: number, name: string) => {
    const next = tableColumns.map((c, idx) => (idx === ci ? name : c));
    setTableColumns(next);
    emitLive({ tableColumns: next });
  };
  const removeTableColumn = (ci: number) => {
    if (tableColumns.length <= 1) return;
    const nextCols = tableColumns.filter((_, idx) => idx !== ci);
    const nextRows = tableRowData.map((r) => ({ ...r, cells: r.cells.filter((_, idx) => idx !== ci) }));
    setTableColumns(nextCols);
    setTableRowData(nextRows);
    emitLive({ tableColumns: nextCols, tableRowData: nextRows });
  };
  const addTableRow = () => {
    const next = [
      ...tableRowData,
      { id: String(tableRowData.length + 1), cells: tableColumns.map(() => '') },
    ];
    setTableRowData(next);
    emitLive({ tableRowData: next });
  };
  const updateTableRowId = (ri: number, id: string) => {
    const next = tableRowData.map((r, idx) => (idx === ri ? { ...r, id } : r));
    setTableRowData(next);
    emitLive({ tableRowData: next });
  };
  const updateTableCell = (ri: number, ci: number, val: string) => {
    const next = tableRowData.map((r, idx) =>
      idx === ri ? { ...r, cells: r.cells.map((c, cIdx) => (cIdx === ci ? val : c)) } : r
    );
    setTableRowData(next);
    emitLive({ tableRowData: next });
  };
  const removeTableRow = (ri: number) => {
    const next = tableRowData.filter((_, idx) => idx !== ri);
    setTableRowData(next);
    emitLive({ tableRowData: next });
  };

  if (activePanelName === 'info') {
    // ── Info tab ──
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Title"
          size="small"
          fullWidth
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            emitLive({ title: e.target.value });
          }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Chart Type</InputLabel>
          <Select
            label="Chart Type"
            value={chartType}
            onChange={(e) => {
              const ct = e.target.value as DashboardChartType;
              setChartType(ct);
              emitLive({ chartType: ct });
            }}
            MenuProps={{ ...THEMED_SELECT_MENU_PROPS }}
          >
            {CHART_TYPES.map((ct) => (
              <MenuItem key={ct.value} value={ct.value}>
                {ct.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ChartThemePicker
          selectedThemeId={chartThemeId}
          onThemeSelect={onThemeSelect}
          onCreateTheme={onCreateTheme}
          categories={categories}
        />
      </Box>
    );
  }

  // ── Data tab ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(chartType === 'bar' ||
        chartType === 'stacked-bar' ||
        chartType === 'multiple-clustered-bar' ||
        chartType === 'multiple-stacked-bar' ||
        chartType === 'multiple-line') && (
        <TextField
          label="X-axis Label"
          size="small"
          fullWidth
          value={xAxisLabel}
          onChange={(e) => {
            setXAxisLabel(e.target.value);
            emitLive({ xAxisLabel: e.target.value });
          }}
        />
      )}
      {(chartType === 'bar' ||
        chartType === 'stacked-bar' ||
        chartType === 'line' ||
        chartType === 'multiple-line' ||
        chartType === 'multiple-clustered-bar' ||
        chartType === 'multiple-stacked-bar') && (
        <TextField
          label="Y-axis Label"
          size="small"
          fullWidth
          value={yAxisLabel}
          onChange={(e) => {
            setYAxisLabel(e.target.value);
            emitLive({ yAxisLabel: e.target.value });
          }}
        />
      )}
      <ButtonGroup size="small" fullWidth>
        <Button
          variant={sourceType === 'static' ? 'contained' : 'outlined'}
          onClick={() => {
            setSourceType('static');
            emitLive({ sourceType: 'static' });
          }}
        >
          Static
        </Button>
        <Button
          variant={sourceType === 'agent' ? 'contained' : 'outlined'}
          onClick={() => {
            setSourceType('agent');
            emitLive({ sourceType: 'agent' });
          }}
        >
          Agent
        </Button>
      </ButtonGroup>

      {sourceType === 'static' &&
        chartType === 'pie' &&
        (() => {
          const total = staticRows.reduce((s, r) => s + (Number(r.value) || 0), 0);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {staticRows.map((row, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Label"
                    size="small"
                    value={row.category}
                    onChange={(e) => updateRow(i, 'category', e.target.value)}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label="%"
                    size="small"
                    type="number"
                    value={String(row.value)}
                    onChange={(e) => updateRow(i, 'value', e.target.value)}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                  <IconButton size="small" onClick={() => removeRow(i)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ alignSelf: 'flex-start' }}>
                Add Slice
              </Button>
              {staticRows.length > 0 && (
                <Typography variant="caption" color={total === 100 ? 'success.main' : 'error'}>
                  Total: {Number.isInteger(total) ? total : total.toFixed(1)}%
                  {total !== 100 ? ' — slices should add up to 100%' : ' ✓'}
                </Typography>
              )}
            </Box>
          );
        })()}

      {sourceType === 'static' && (chartType === 'bar' || chartType === 'stacked-bar') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {staticRows.map((row, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Bar Label"
                size="small"
                value={row.category}
                onChange={(e) => updateRow(i, 'category', e.target.value)}
                sx={{ flex: 2 }}
              />
              <TextField
                label="Value"
                size="small"
                type="number"
                value={String(row.value)}
                onChange={(e) => updateRow(i, 'value', e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton size="small" onClick={() => removeRow(i)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ alignSelf: 'flex-start' }}>
            Add Bar
          </Button>
        </Box>
      )}

      {sourceType === 'static' &&
        (chartType === 'multiple-clustered-bar' ||
          chartType === 'multiple-stacked-bar' ||
          chartType === 'multiple-line') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {staticSeries.map((series, si) => (
              <Box
                key={si}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Series Name"
                    size="small"
                    value={series.name}
                    onChange={(e) => updateSeriesName(si, e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" onClick={() => removeSeries(si)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                {series.rows.map((row, ri) => (
                  <Box key={ri} sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: 1 }}>
                    <TextField
                      label="Category"
                      size="small"
                      value={row.category}
                      onChange={(e) => updateSeriesRow(si, ri, 'category', e.target.value)}
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      label="Value"
                      size="small"
                      type="number"
                      value={String(row.value)}
                      onChange={(e) => updateSeriesRow(si, ri, 'value', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <IconButton size="small" onClick={() => removeSeriesRow(si, ri)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addSeriesRow(si)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Data Point
                </Button>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addSeries} sx={{ alignSelf: 'flex-start' }}>
              Add Series
            </Button>
          </Box>
        )}

      {sourceType === 'static' && chartType === 'kpi-widget' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {staticRows.map((row, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Label"
                size="small"
                value={row.category}
                onChange={(e) => updateRow(i, 'category', e.target.value)}
                sx={{ flex: 2 }}
              />
              <TextField
                label="Display Value"
                size="small"
                value={String(row.value)}
                onChange={(e) => updateRow(i, 'value', e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton size="small" onClick={() => removeRow(i)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ alignSelf: 'flex-start' }}>
            Add Metric
          </Button>
        </Box>
      )}

      {sourceType === 'static' &&
        (chartType === 'progress-bar-widget' || chartType === 'semi-circle-progress-widget') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {staticRows.map((row, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Label"
                  size="small"
                  value={row.category}
                  onChange={(e) => updateRow(i, 'category', e.target.value)}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="% (0–100)"
                  size="small"
                  type="number"
                  value={String(row.value)}
                  onChange={(e) => updateRow(i, 'value', e.target.value)}
                  sx={{ flex: 1 }}
                  inputProps={{ min: 0, max: 100 }}
                />
                <IconButton size="small" onClick={() => removeRow(i)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ alignSelf: 'flex-start' }}>
              Add Item
            </Button>
          </Box>
        )}

      {sourceType === 'static' && chartType === 'data-table' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Column definitions */}
          <Typography variant="caption" color="text.secondary">
            Columns
          </Typography>
          {tableColumns.map((col, ci) => (
            <Box key={ci} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label={`Column ${ci + 1}`}
                size="small"
                value={col}
                onChange={(e) => updateTableColumnName(ci, e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => removeTableColumn(ci)}
                disabled={tableColumns.length <= 1}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addTableColumn}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Column
          </Button>

          <Divider />

          {/* Row data */}
          <Typography variant="caption" color="text.secondary">
            Rows
          </Typography>
          {tableRowData.map((row, ri) => (
            <Box
              key={ri}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Row ID"
                  size="small"
                  value={row.id}
                  onChange={(e) => updateTableRowId(ri, e.target.value)}
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => removeTableRow(ri)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              {tableColumns.map((colName, ci) => (
                <TextField
                  key={ci}
                  label={colName || `Column ${ci + 1}`}
                  size="small"
                  value={row.cells[ci] ?? ''}
                  onChange={(e) => updateTableCell(ri, ci, e.target.value)}
                  fullWidth
                />
              ))}
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addTableRow} sx={{ alignSelf: 'flex-start' }}>
            Add Row
          </Button>
        </Box>
      )}

      {sourceType === 'agent' && (
        <AgentDataSection
          agentId={agentAgent.agentId}
          setAgentId={agentAgent.setAgentId}
          agentIdError={agentAgent.agentIdError}
          extensionPath={agentAgent.extensionPath}
          setExtensionPath={agentAgent.setExtensionPath}
          extensionPathError={agentAgent.extensionPathError}
          fetchState={agentAgent.fetchState}
          fetchError={agentAgent.fetchError}
          validateAgentId={agentAgent.validateAgentId}
          validateExtensionPath={agentAgent.validateExtensionPath}
          handleFetchPreview={agentAgent.handleFetchPreview}
          agentDataExample={agentDataExample}
          agents={agentAgent.agents}
        />
      )}
    </Box>
  );
}

// ─── TextConfigForm ───────────────────────────────────────────────────────────

function TextConfigForm({
  widget,
  onUpdate,
  onUpdateDataSource,
  commitRef,
  committedRef,
  activeTab,
  onLiveUpdate,
}: FormProps) {
  const { registry } = useWidgetRegistry();
  const entry = registry.get(widget.type);
  const cfg = widget.config as TextWidgetConfig;
  const ds = widget.dataSource;

  const [title, setTitle] = useState(cfg.title ?? '');
  const [textMode, setTextMode] = useState<'richtext' | 'markdown'>(cfg.textMode ?? 'richtext');

  // Initialise content from dataSource.data so edits persist across re-opens
  const initContent = (() => {
    if (ds?.type === 'static' && ds.data?.type === 'text') {
      const raw = ds.data.data.content ?? '';
      const mode = cfg.textMode ?? 'richtext';
      if (mode === 'markdown') return { richtext: EMPTY_DOC_NODE, markdown: raw };
      try {
        return { richtext: JSON.parse(raw) as RemirrorTypes.RootNode, markdown: '' };
      } catch {
        /* fall through */
      }
    }
    return { richtext: EMPTY_DOC_NODE, markdown: '' };
  })();
  const [richtextContent, setRichtextContent] = useState<RemirrorTypes.RootNode>(initContent.richtext);
  const [markdownContent, setMarkdownContent] = useState(initContent.markdown);
  const [sourceType, setSourceType] = useState<'static' | 'agent'>(ds?.type === 'agent' ? 'agent' : 'static');
  const agentAgent = useAgentState(
    widget.id,
    ds?.type === 'agent' ? ds.agentId : 0,
    ds?.type === 'agent' ? ds.extensionPath : ''
  );

  const emitLive = useCallback(
    (
      overrides: {
        title?: string;
        textMode?: 'richtext' | 'markdown';
        content?: RemirrorTypes.RootNode;
        markdownContent?: string;
        sourceType?: 'static' | 'agent';
      } = {}
    ) => {
      const t = overrides.title ?? title;
      const tm = overrides.textMode ?? textMode;
      const rc = overrides.content ?? richtextContent;
      const md = overrides.markdownContent ?? markdownContent;
      const st = overrides.sourceType ?? sourceType;
      const liveConfig: Partial<TextWidgetConfig> = { title: t, textMode: tm };
      let liveDs: LiveDataSource;
      if (st === 'agent') {
        liveDs = { type: 'agent', agentId: agentAgent.agentId, extensionPath: agentAgent.extensionPath };
      } else {
        const serialized = tm === 'markdown' ? md : JSON.stringify(rc);
        liveDs = { type: 'static', data: { type: 'text', data: { content: serialized } } };
      }
      onLiveUpdate(liveConfig, liveDs);
    },
    [
      title,
      textMode,
      richtextContent,
      markdownContent,
      sourceType,
      agentAgent.agentId,
      agentAgent.extensionPath,
      onLiveUpdate,
    ]
  );

  const handleRichtextChange: RemirrorEventListener<BasicMarkupEditorExtensions> = useCallback(
    ({ state }) => {
      const doc = state.doc.toJSON() as RemirrorTypes.RootNode;
      setRichtextContent(doc);
      emitLive({ content: doc });
    },
    [emitLive]
  );

  const agentDataExample = entry?.getAgentDataExample?.(cfg as WidgetSpecificConfig);

  commitRef.current = () => {
    if (sourceType === 'agent') {
      if (!agentAgent.agentId || agentAgent.agentId <= 0) return 'Enter a valid Agent ID.';
      if (!agentAgent.extensionPath.trim()) return 'Enter an extension path.';
      if (!agentDataExample) return 'Widget data model is not configured.';
    }

    const newConfig: Partial<TextWidgetConfig> = { title, textMode };
    const model = agentDataExample;
    let newDs: WidgetDataSource;
    if (sourceType === 'agent' && model) {
      newDs = {
        type: 'agent',
        agentId: agentAgent.agentId,
        extensionPath: agentAgent.extensionPath,
        dataModel: model,
      };
    } else {
      const serialized = textMode === 'markdown' ? markdownContent : JSON.stringify(richtextContent);
      newDs = { type: 'static', data: { type: 'text', data: { content: serialized } } };
    }

    committedRef.current = { config: newConfig, dataSource: newDs };
    onUpdate(newConfig);
    onUpdateDataSource(newDs);
    return null;
  };

  if (activeTab === 0) {
    // ── Info tab ──
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Title"
          size="small"
          fullWidth
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            emitLive({ title: e.target.value });
          }}
        />
        <ButtonGroup size="small" fullWidth>
          <Button
            variant={textMode === 'richtext' ? 'contained' : 'outlined'}
            onClick={() => {
              setTextMode('richtext');
              emitLive({ textMode: 'richtext' });
            }}
          >
            Rich Text
          </Button>
          <Button
            variant={textMode === 'markdown' ? 'contained' : 'outlined'}
            onClick={() => {
              setTextMode('markdown');
              emitLive({ textMode: 'markdown' });
            }}
          >
            Markdown
          </Button>
        </ButtonGroup>
      </Box>
    );
  }

  // ── Data tab ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ButtonGroup size="small" fullWidth>
        <Button
          variant={sourceType === 'static' ? 'contained' : 'outlined'}
          onClick={() => {
            setSourceType('static');
            emitLive({ sourceType: 'static' });
          }}
        >
          Static
        </Button>
        <Button
          variant={sourceType === 'agent' ? 'contained' : 'outlined'}
          onClick={() => {
            setSourceType('agent');
            emitLive({ sourceType: 'agent' });
          }}
        >
          Agent
        </Button>
      </ButtonGroup>

      {sourceType === 'static' && (
        <>
          {textMode === 'richtext' ? (
            <BasicMarkupEditor
              toolbarClassName={toolbarNoBorder}
              content={richtextContent}
              onChange={handleRichtextChange}
            />
          ) : (
            <TextField
              label="Markdown content"
              multiline
              minRows={6}
              size="small"
              fullWidth
              value={markdownContent}
              onChange={(e) => {
                setMarkdownContent(e.target.value);
                emitLive({ markdownContent: e.target.value });
              }}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
          )}
        </>
      )}

      {sourceType === 'agent' && (
        <AgentDataSection
          agentId={agentAgent.agentId}
          setAgentId={agentAgent.setAgentId}
          agentIdError={agentAgent.agentIdError}
          extensionPath={agentAgent.extensionPath}
          setExtensionPath={agentAgent.setExtensionPath}
          extensionPathError={agentAgent.extensionPathError}
          fetchState={agentAgent.fetchState}
          fetchError={agentAgent.fetchError}
          validateAgentId={agentAgent.validateAgentId}
          validateExtensionPath={agentAgent.validateExtensionPath}
          handleFetchPreview={agentAgent.handleFetchPreview}
          agentDataExample={agentDataExample}
          agents={agentAgent.agents}
        />
      )}
    </Box>
  );
}

// ─── MetricConfigForm ─────────────────────────────────────────────────────────

function MetricConfigForm({
  widget,
  onUpdate,
  onUpdateDataSource,
  commitRef,
  committedRef,
  activeTab,
  onLiveUpdate,
}: FormProps) {
  const { registry } = useWidgetRegistry();
  const entry = registry.get(widget.type);
  const cfg = widget.config as MetricCardWidgetConfig;
  const ds = widget.dataSource;

  const [title, setTitle] = useState(cfg.title ?? '');
  const [backgroundColor, setBackgroundColor] = useState(cfg.backgroundColor ?? '');
  const [kpis, setKpis] = useState<KpiItem[]>(() => metricKpisFromDataSource(ds));
  const [sourceType, setSourceType] = useState<'static' | 'agent'>(ds?.type === 'agent' ? 'agent' : 'static');
  const agentAgent = useAgentState(
    widget.id,
    ds?.type === 'agent' ? ds.agentId : 0,
    ds?.type === 'agent' ? ds.extensionPath : ''
  );

  const emitLive = useCallback(
    (
      overrides: {
        title?: string;
        backgroundColor?: string;
        kpis?: KpiItem[];
        sourceType?: 'static' | 'agent';
      } = {}
    ) => {
      const t = overrides.title ?? title;
      const bg = overrides.backgroundColor ?? backgroundColor;
      const k = overrides.kpis ?? kpis;
      const st = overrides.sourceType ?? sourceType;
      const liveConfig: Partial<MetricCardWidgetConfig> = { title: t, backgroundColor: bg || undefined };
      let liveDs: LiveDataSource;
      if (st === 'agent') {
        liveDs = { type: 'agent', agentId: agentAgent.agentId, extensionPath: agentAgent.extensionPath };
      } else {
        liveDs = {
          type: 'static',
          data: {
            type: 'metric_card',
            data: {
              values: k.map((ki) => `${ki.value}${ki.unit ? ` ${ki.unit}` : ''}`),
              labels: k.map((ki) => ki.label),
            },
          },
        };
      }
      onLiveUpdate(liveConfig, liveDs);
    },
    [title, backgroundColor, kpis, sourceType, agentAgent.agentId, agentAgent.extensionPath, onLiveUpdate]
  );

  const agentDataExample = entry?.getAgentDataExample?.(cfg as WidgetSpecificConfig);

  commitRef.current = () => {
    if (sourceType === 'static' && kpis.filter((k) => k.label.trim() || k.value.trim()).length === 0) {
      return 'Add at least one KPI before saving.';
    }
    if (sourceType === 'agent') {
      if (!agentAgent.agentId || agentAgent.agentId <= 0) return 'Enter a valid Agent ID.';
      if (!agentAgent.extensionPath.trim()) return 'Enter an extension path.';
      if (!agentDataExample) return 'Widget data model is not configured.';
    }

    const newConfig: Partial<MetricCardWidgetConfig> = {
      title,
      backgroundColor: backgroundColor || undefined,
    };
    const model = agentDataExample;
    let newDs: WidgetDataSource;
    if (sourceType === 'agent' && model) {
      newDs = {
        type: 'agent',
        agentId: agentAgent.agentId,
        extensionPath: agentAgent.extensionPath,
        dataModel: model,
      };
    } else {
      newDs = {
        type: 'static',
        data: {
          type: 'metric_card',
          data: {
            values: kpis.map((k) => `${k.value}${k.unit ? ` ${k.unit}` : ''}`),
            labels: kpis.map((k) => k.label),
          },
        },
      };
    }

    committedRef.current = { config: newConfig, dataSource: newDs };
    onUpdate(newConfig);
    onUpdateDataSource(newDs);
    return null;
  };

  const addKpi = () => {
    const next = [...kpis, { label: '', value: '', unit: '' }];
    setKpis(next);
    emitLive({ kpis: next });
  };
  const updateKpi = (i: number, field: keyof KpiItem, val: string) => {
    const next = kpis.map((k, idx) => (idx === i ? { ...k, [field]: val } : k));
    setKpis(next);
    emitLive({ kpis: next });
  };
  const removeKpi = (i: number) => {
    const next = kpis.filter((_, idx) => idx !== i);
    setKpis(next);
    emitLive({ kpis: next });
  };

  if (activeTab === 0) {
    // ── Info tab ──
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Title"
          size="small"
          fullWidth
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            emitLive({ title: e.target.value });
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            label="Background Color"
            size="small"
            value={backgroundColor}
            onChange={(e) => {
              setBackgroundColor(e.target.value);
              emitLive({ backgroundColor: e.target.value });
            }}
            placeholder="#ffffff"
            sx={{ flex: 1 }}
          />
          <input
            type="color"
            value={backgroundColor || '#ffffff'}
            onChange={(e) => {
              setBackgroundColor(e.target.value);
              emitLive({ backgroundColor: e.target.value });
            }}
            style={{
              width: 36,
              height: 36,
              padding: 0,
              border: '1px solid #ccc',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Displays one or more KPI metrics as labelled value cards.
        </Typography>
      </Box>
    );
  }

  // ── Data tab ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ButtonGroup size="small" fullWidth>
        <Button
          variant={sourceType === 'static' ? 'contained' : 'outlined'}
          onClick={() => {
            setSourceType('static');
            emitLive({ sourceType: 'static' });
          }}
        >
          Static
        </Button>
        <Button
          variant={sourceType === 'agent' ? 'contained' : 'outlined'}
          onClick={() => {
            setSourceType('agent');
            emitLive({ sourceType: 'agent' });
          }}
        >
          Agent
        </Button>
      </ButtonGroup>

      {sourceType === 'static' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {kpis.map((kpi, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Label"
                size="small"
                value={kpi.label}
                onChange={(e) => updateKpi(i, 'label', e.target.value)}
                sx={{ flex: 2 }}
              />
              <TextField
                label="Value"
                size="small"
                value={kpi.value}
                onChange={(e) => updateKpi(i, 'value', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Unit"
                size="small"
                value={kpi.unit}
                onChange={(e) => updateKpi(i, 'unit', e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton size="small" onClick={() => removeKpi(i)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addKpi} sx={{ alignSelf: 'flex-start' }}>
            Add KPI
          </Button>
        </Box>
      )}

      {sourceType === 'agent' && (
        <AgentDataSection
          agentId={agentAgent.agentId}
          setAgentId={agentAgent.setAgentId}
          agentIdError={agentAgent.agentIdError}
          extensionPath={agentAgent.extensionPath}
          setExtensionPath={agentAgent.setExtensionPath}
          extensionPathError={agentAgent.extensionPathError}
          fetchState={agentAgent.fetchState}
          fetchError={agentAgent.fetchError}
          validateAgentId={agentAgent.validateAgentId}
          validateExtensionPath={agentAgent.validateExtensionPath}
          handleFetchPreview={agentAgent.handleFetchPreview}
          agentDataExample={agentDataExample}
          agents={agentAgent.agents}
        />
      )}
    </Box>
  );
}

// ─── CodeWidgetFullScreen ──────────────────────────────────────────────────────

// ─── useCodeWidgetState ───────────────────────────────────────────────────────

/**
 * Shared state and helpers for code widget config forms.
 * Used by both CodeWidgetFullScreen (create mode) and CodeConfigForm (edit mode).
 */
type CodeWidget = Extract<WidgetConfig, { type: 'code' }>;

/** Narrows a WidgetConfig to a code widget. Only call when the widget is known to be type 'code'. */
function asCodeWidget(widget: WidgetConfig): CodeWidget {
  if (widget.type !== 'code') throw new Error(`Expected code widget, got "${widget.type}"`);
  return widget;
}

function useCodeWidgetState(widget: WidgetConfig) {
  const { registry } = useWidgetRegistry();
  const entry = registry.get(widget.type);
  const codeWidget = asCodeWidget(widget);
  const cfg = codeWidget.config;
  const ds = codeWidget.dataSource;

  // State for UI rendering (CodeConfigForm needs these for controlled inputs)
  const [title, _setTitle] = useState(cfg.title ?? '');
  const [jsx, _setJsx] = useState(cfg.jsx ?? '');
  const [sourceType, _setSourceType] = useState<'static' | 'agent'>(
    ds?.type === 'agent' ? 'agent' : 'static'
  );
  // Initialize data from whichever source is active:
  // - Static: use the stored inline data
  // - Agent: use the saved dataModel (the expected response shape) so the editor
  //   shows the data structure the user's code was written against
  const [staticData, _setStaticData] = useState<Record<string, unknown>>(() => {
    if (ds?.type === 'static' && ds.data?.type === 'code') {
      return ds.data.data;
    }
    if (ds?.type === 'agent' && ds.dataModel?.type === 'code') {
      return ds.dataModel.data;
    }
    return {};
  });

  // Parallel refs — updated synchronously so buildConfig/buildDataSource always
  // read the latest values, even when called before React re-renders (e.g. when
  // handleModalSave sets state then immediately calls onCommit → commitRef).
  const titleRef = useRef(title);
  const jsxRef = useRef(jsx);
  const sourceTypeRef = useRef(sourceType);
  const staticDataRef = useRef(staticData);

  const setTitle = useCallback((v: string) => {
    titleRef.current = v;
    _setTitle(v);
  }, []);
  const setJsx = useCallback((v: string) => {
    jsxRef.current = v;
    _setJsx(v);
  }, []);
  const setSourceType = useCallback((v: 'static' | 'agent') => {
    sourceTypeRef.current = v;
    _setSourceType(v);
  }, []);
  const setStaticData = useCallback((v: Record<string, unknown>) => {
    staticDataRef.current = v;
    _setStaticData(v);
  }, []);

  // Reset all local state when the selected widget changes without unmounting.
  const prevWidgetId = useRef(widget.id);
  useEffect(() => {
    if (prevWidgetId.current === widget.id) return;
    prevWidgetId.current = widget.id;

    const newCfg = asCodeWidget(widget).config;
    const newDs = widget.dataSource;

    setTitle(newCfg.title ?? '');
    setJsx(newCfg.jsx ?? '');
    setSourceType(newDs?.type === 'agent' ? 'agent' : 'static');

    if (newDs?.type === 'static' && newDs.data?.type === 'code') {
      setStaticData(newDs.data.data);
    } else if (newDs?.type === 'agent' && newDs.dataModel?.type === 'code') {
      setStaticData(newDs.dataModel.data);
    } else {
      setStaticData({});
    }
  }, [widget.id, widget.config, widget.dataSource, setTitle, setJsx, setSourceType, setStaticData]);

  const agent = useAgentState(
    widget.id,
    ds?.type === 'agent' ? ds.agentId : 0,
    ds?.type === 'agent' ? ds.extensionPath : ''
  );
  const agentDataExample = entry?.getAgentDataExample?.(cfg as WidgetSpecificConfig);

  // Auto-fetch agent data so /data.js is populated with live data
  // when the user opens the editor for an existing agent-sourced widget.
  // Keyed off widget.id + agent fields so it re-runs if the user switches widgets.
  const lastAutoFetchKey = useRef('');
  const { onDataFetchedRef, handleFetchPreview } = agent;
  useEffect(() => {
    if (ds?.type !== 'agent' || !ds.agentId || !ds.extensionPath) return;
    const key = `${widget.id}:${ds.agentId}:${ds.extensionPath}`;
    if (lastAutoFetchKey.current === key) return;
    lastAutoFetchKey.current = key;

    // Wire a one-time listener to update staticData when the fetch completes,
    // so the modal opens with live data even if it wasn't open during the fetch.
    let unmounted = false;
    const wrapper = (fetchedData: Record<string, unknown>): void => {
      if (unmounted) return;
      setStaticData(fetchedData);
      // Self-remove: only restore if ref still points to this wrapper.
      // If the modal installed its own listener in the meantime, leave it intact.
      if (onDataFetchedRef.current === wrapper) {
        onDataFetchedRef.current = null;
      }
    };
    onDataFetchedRef.current = wrapper;
    void handleFetchPreview();
    return () => {
      unmounted = true;
      // Only clean up if ref still points to our wrapper — another consumer
      // (e.g. the code editor modal) may have replaced it.
      if (onDataFetchedRef.current === wrapper) {
        onDataFetchedRef.current = null;
      }
    };
  }, [widget.id, ds, onDataFetchedRef, handleFetchPreview, setStaticData]);

  /** Validate agent fields; returns error string or null */
  const validateAgent = useCallback((): string | null => {
    if (sourceTypeRef.current !== 'agent') return null;
    if (!agent.agentId || agent.agentId <= 0) return 'Enter a valid Agent ID.';
    if (!agent.extensionPath.trim()) return 'Enter an extension path.';
    if (!agentDataExample) return 'Widget data model is not configured.';
    return null;
  }, [agent.agentId, agent.extensionPath, agentDataExample]);

  /** Build a config partial — reads from refs so it's always current */
  const buildConfig = useCallback(
    (overrides?: { title?: string; jsx?: string }): Partial<CodeWidgetConfig> => ({
      title: overrides?.title ?? titleRef.current,
      jsx: overrides?.jsx ?? jsxRef.current,
    }),
    []
  );

  /** Build a WidgetDataSource — reads from refs so it's always current */
  const buildDataSource = useCallback(
    (overrides?: {
      sourceType?: 'static' | 'agent';
      staticData?: Record<string, unknown>;
    }): WidgetDataSource => {
      const st = overrides?.sourceType ?? sourceTypeRef.current;
      const sd = overrides?.staticData ?? staticDataRef.current;
      if (st === 'agent' && agentDataExample) {
        return {
          type: 'agent',
          agentId: agent.agentId,
          extensionPath: agent.extensionPath,
          dataModel: agentDataExample,
        };
      }
      return { type: 'static', data: { type: 'code', data: sd } };
    },
    [agentDataExample, agent.agentId, agent.extensionPath]
  );

  return {
    cfg,
    title,
    setTitle,
    jsx,
    setJsx,
    sourceType,
    setSourceType,
    staticData,
    setStaticData,
    agent,
    agentDataExample,
    validateAgent,
    buildConfig,
    buildDataSource,
  };
}

// ─── CodeWidgetFullScreen ─────────────────────────────────────────────────────

/**
 * For code widgets, we skip the sidebar entirely and go straight to the
 * full-screen code editor modal. This component auto-opens the modal and
 * wires save/close into the existing widget create/edit flow.
 */
function CodeWidgetFullScreen({
  widget,
  isCreateMode,
  onUpdate,
  onUpdateDataSource,
  onClose,
  onCommit,
  commitRef,
  committedRef,
  onLiveUpdate,
}: {
  widget: WidgetConfig;
  isCreateMode: boolean;
  onUpdate: (partial: Partial<WidgetConfig['config']>) => void;
  onUpdateDataSource: (ds: WidgetDataSource) => void;
  onClose: () => void;
  onCommit: () => void;
  commitRef: React.MutableRefObject<(() => string | null) | null>;
  committedRef: React.MutableRefObject<CommittedValues | null>;
  onLiveUpdate: (config: Record<string, unknown>, dataSource: LiveDataSource) => void;
}): React.ReactElement {
  const s = useCodeWidgetState(widget);

  commitRef.current = () => {
    const err = s.validateAgent();
    if (err) return err;
    const newConfig = s.buildConfig();
    const newDs = s.buildDataSource();
    committedRef.current = { config: newConfig, dataSource: newDs };
    onUpdate(newConfig);
    onUpdateDataSource(newDs);
    return null;
  };

  const { setTitle, setJsx, setStaticData, setSourceType, buildConfig, buildDataSource } = s;
  const handleModalSave = useCallback(
    (
      newTitle: string,
      newJsx: string,
      newData: Record<string, unknown>,
      newSourceType: 'static' | 'agent'
    ) => {
      setTitle(newTitle);
      setJsx(newJsx);
      setStaticData(newData);
      setSourceType(newSourceType);
      const newConfig = buildConfig({ title: newTitle, jsx: newJsx });
      const newDs = buildDataSource({ sourceType: newSourceType, staticData: newData });
      committedRef.current = { config: newConfig, dataSource: newDs };
      onUpdate(newConfig);
      onUpdateDataSource(newDs);
      // Cast: WidgetDataSource → LiveDataSource is a valid widening (LiveDataSource.data is nullable)
      onLiveUpdate(newConfig, newDs as LiveDataSource);
      onCommit();
    },
    [
      setTitle,
      setJsx,
      setStaticData,
      setSourceType,
      buildConfig,
      buildDataSource,
      onUpdate,
      onUpdateDataSource,
      onLiveUpdate,
      onCommit,
      committedRef,
    ]
  );

  return (
    <CodeEditorModal
      open
      config={s.cfg}
      staticData={s.staticData}
      sourceType={s.sourceType}
      agentConfig={s.agent}
      onSave={handleModalSave}
      onClose={onClose}
    />
  );
}

// ─── CodeConfigForm ────────────────────────────────────────────────────────────

function CodeConfigForm({
  widget,
  onUpdate,
  onUpdateDataSource,
  commitRef,
  committedRef,
  activeTab,
  onLiveUpdate,
}: FormProps) {
  const s = useCodeWidgetState(widget);
  const [editorOpen, setEditorOpen] = useState(false);

  const { buildConfig: sBuildConfig, sourceType: sSourceType, staticData: sStaticData, agent: sAgent } = s;
  const emitLive = useCallback(
    (
      overrides: {
        title?: string;
        jsx?: string;
        sourceType?: 'static' | 'agent';
        staticData?: Record<string, unknown>;
      } = {}
    ) => {
      const config = sBuildConfig({ title: overrides.title, jsx: overrides.jsx });
      const st = overrides.sourceType ?? sSourceType;
      const sd = overrides.staticData ?? sStaticData;
      const liveDs: LiveDataSource =
        st === 'agent'
          ? { type: 'agent', agentId: sAgent.agentId, extensionPath: sAgent.extensionPath }
          : { type: 'static', data: { type: 'code', data: sd } };
      onLiveUpdate(config, liveDs);
    },
    [sBuildConfig, sSourceType, sStaticData, sAgent.agentId, sAgent.extensionPath, onLiveUpdate]
  );

  commitRef.current = () => {
    const err = s.validateAgent();
    if (err) return err;
    const newConfig = s.buildConfig();
    const newDs = s.buildDataSource();
    committedRef.current = { config: newConfig, dataSource: newDs };
    onUpdate(newConfig);
    onUpdateDataSource(newDs);
    return null;
  };

  const {
    setTitle: sSetTitle,
    setJsx: sSetJsx,
    setStaticData: sSetStaticData,
    setSourceType: sSetSourceType,
  } = s;
  const handleModalSave = useCallback(
    (
      newTitle: string,
      newJsx: string,
      newData: Record<string, unknown>,
      newSourceType: 'static' | 'agent'
    ) => {
      sSetTitle(newTitle);
      sSetJsx(newJsx);
      sSetStaticData(newData);
      sSetSourceType(newSourceType);
      emitLive({ title: newTitle, jsx: newJsx, staticData: newData, sourceType: newSourceType });
    },
    [sSetTitle, sSetJsx, sSetStaticData, sSetSourceType, emitLive]
  );

  if (activeTab === 0) {
    // ── Info tab ──
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Title"
          size="small"
          fullWidth
          value={s.title}
          onChange={(e) => {
            s.setTitle(e.target.value);
            emitLive({ title: e.target.value });
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Write a React component with Tailwind CSS. Import injected data via{' '}
          <code>{"import { data } from './data'"}</code>.
        </Typography>
      </Box>
    );
  }

  // ── Data tab ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Button variant="outlined" fullWidth onClick={() => setEditorOpen(true)}>
        Edit Code & Data
      </Button>

      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}
      >
        Data Source
      </Typography>
      <ButtonGroup size="small" fullWidth>
        <Button
          variant={s.sourceType === 'static' ? 'contained' : 'outlined'}
          onClick={() => {
            s.setSourceType('static');
            emitLive({ sourceType: 'static' });
          }}
          sx={{ textTransform: 'none' }}
        >
          Static
        </Button>
        <Button
          variant={s.sourceType === 'agent' ? 'contained' : 'outlined'}
          onClick={() => {
            s.setSourceType('agent');
            emitLive({ sourceType: 'agent' });
          }}
          sx={{ textTransform: 'none' }}
        >
          Agent
        </Button>
      </ButtonGroup>

      {s.sourceType === 'static' && (
        <JsonEditor
          data={s.staticData}
          onUpdate={({ newData }) => {
            const updated = newData as Record<string, unknown>;
            s.setStaticData(updated);
            emitLive({ staticData: updated });
          }}
          collapse={2}
          rootName="data"
        />
      )}

      {s.sourceType === 'agent' && (
        <Typography variant="caption" color="text.secondary">
          Agent data source is configured in the full editor. Click "Edit Code & Data" above.
        </Typography>
      )}

      <CodeEditorModal
        open={editorOpen}
        config={{ ...s.cfg, title: s.title, jsx: s.jsx }}
        staticData={s.staticData}
        sourceType={s.sourceType}
        agentConfig={s.agent}
        onSave={handleModalSave}
        onClose={() => setEditorOpen(false)}
      />
    </Box>
  );
}

// ─── RepDashboardConfigForm ───────────────────────────────────────────────────

function RepDashboardConfigForm({
  widget,
  onUpdate,
  onUpdateDataSource,
  commitRef,
  committedRef,
  activeTab,
  onLiveUpdate,
}: FormProps) {
  const { registry } = useWidgetRegistry();
  const entry = registry.get(widget.type);
  const cfg = widget.config as RepDashboardWidgetConfig;
  const ds = widget.dataSource;

  // JSON editor state — initialise from existing static data or the agent example
  const initialJson = (() => {
    if (ds?.type === 'static' && ds.data?.type === 'rep_dashboard') {
      return JSON.stringify(ds.data.data, null, 2);
    }
    const example = entry?.getAgentDataExample?.(cfg);
    if (example?.type === 'rep_dashboard') {
      return JSON.stringify(example.data, null, 2);
    }
    return '{}';
  })();

  const [title, setTitle] = useState(cfg.title ?? '');
  const [headerColor, setHeaderColor] = useState(cfg.headerColor ?? '#001845');
  const [accentColor, setAccentColor] = useState(cfg.accentColor ?? '#FF5757');
  const [defaultRowsPerPage, setDefaultRowsPerPage] = useState(cfg.defaultRowsPerPage ?? 5);
  const [jsonText, setJsonText] = useState(initialJson);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Agent state
  const agentAgent = useAgentState(
    widget.id,
    ds?.type === 'agent' ? ds.agentId : 0,
    ds?.type === 'agent' ? ds.extensionPath : ''
  );
  const agentDataExample = useMemo(() => entry?.getAgentDataExample?.(cfg) ?? null, [entry, cfg]);
  const sourceType = ds?.type === 'agent' ? 'agent' : 'static';

  const emitLive = useCallback(
    (overrides?: {
      title?: string;
      headerColor?: string;
      accentColor?: string;
      defaultRowsPerPage?: number;
      jsonText?: string;
    }) => {
      const t = overrides?.title ?? title;
      const hc = overrides?.headerColor ?? headerColor;
      const ac = overrides?.accentColor ?? accentColor;
      const rpp = overrides?.defaultRowsPerPage ?? defaultRowsPerPage;
      const jt = overrides?.jsonText ?? jsonText;

      const config: Partial<RepDashboardWidgetConfig> = {
        title: t,
        headerColor: hc,
        accentColor: ac,
        defaultRowsPerPage: rpp,
      };

      let liveDs: LiveDataSource = { type: 'static', data: null };
      try {
        const parsed = JSON.parse(jt);
        const result = RepDashboardWidgetDataSchema.safeParse(parsed);
        if (result.success) {
          liveDs = { type: 'static', data: { type: 'rep_dashboard', data: result.data } };
        }
      } catch {
        /* invalid json — leave data null */
      }

      onLiveUpdate(config, liveDs);
    },
    [title, headerColor, accentColor, defaultRowsPerPage, jsonText, onLiveUpdate]
  );

  // Commit handler
  commitRef.current = () => {
    const configOut: RepDashboardWidgetConfig = {
      title: title.trim() || 'Rep Dashboard',
      headerColor,
      accentColor,
      defaultRowsPerPage,
    };

    let dataSource: WidgetDataSource;
    if (sourceType === 'agent' && agentAgent.agentId > 0 && agentAgent.extensionPath) {
      dataSource = {
        type: 'agent',
        agentId: agentAgent.agentId,
        extensionPath: agentAgent.extensionPath,
        dataModel: agentDataExample ?? {
          type: 'rep_dashboard',
          data: { summaryText: '', kpis: [], opportunities: [], tableRows: [] },
        },
      };
    } else {
      // Validate JSON
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonText);
      } catch {
        return 'Invalid JSON syntax';
      }
      const result = RepDashboardWidgetDataSchema.safeParse(parsed);
      if (!result.success) {
        return `Invalid data: ${result.error.issues.map((i) => i.message).join(', ')}`;
      }
      dataSource = { type: 'static', data: { type: 'rep_dashboard', data: result.data } };
    }

    committedRef.current = { config: configOut, dataSource };
    return null;
  };

  // Info tab
  const infoTab = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Title"
        size="small"
        fullWidth
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          emitLive({ title: e.target.value });
        }}
      />
      <TextField
        label="Header Color"
        size="small"
        fullWidth
        value={headerColor}
        onChange={(e) => {
          setHeaderColor(e.target.value);
          emitLive({ headerColor: e.target.value });
        }}
      />
      <TextField
        label="Accent Color"
        size="small"
        fullWidth
        value={accentColor}
        onChange={(e) => {
          setAccentColor(e.target.value);
          emitLive({ accentColor: e.target.value });
        }}
      />
      <TextField
        label="Default Rows Per Page"
        size="small"
        fullWidth
        type="number"
        value={defaultRowsPerPage}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10) || 5;
          setDefaultRowsPerPage(v);
          emitLive({ defaultRowsPerPage: v });
        }}
      />
    </Box>
  );

  // Data tab
  const dataTab = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ButtonGroup size="small" fullWidth>
        <Button
          variant={sourceType === 'static' ? 'contained' : 'outlined'}
          onClick={() => {
            if (sourceType !== 'static')
              onUpdateDataSource({
                type: 'static',
                data: {
                  type: 'rep_dashboard',
                  data: { summaryText: '', kpis: [], opportunities: [], tableRows: [] },
                },
              });
          }}
        >
          Static
        </Button>
        <Button
          variant={sourceType === 'agent' ? 'contained' : 'outlined'}
          onClick={() => {
            if (sourceType !== 'agent') {
              onUpdateDataSource({
                type: 'agent',
                agentId: 0,
                extensionPath: '',
                dataModel: agentDataExample ?? {
                  type: 'rep_dashboard',
                  data: { summaryText: '', kpis: [], opportunities: [], tableRows: [] },
                },
              });
            }
          }}
        >
          Agent
        </Button>
      </ButtonGroup>

      {sourceType === 'static' && (
        <>
          <Typography variant="caption" color="text.secondary">
            Paste or edit the JSON data model below. Must conform to the RepDashboardWidgetData schema.
          </Typography>
          <textarea
            value={jsonText}
            onChange={(e) => {
              const val = e.target.value;
              setJsonText(val);
              try {
                const parsed = JSON.parse(val);
                const result = RepDashboardWidgetDataSchema.safeParse(parsed);
                setJsonError(
                  result.success
                    ? null
                    : result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('\n')
                );
              } catch {
                setJsonError('Invalid JSON syntax');
              }
              emitLive({ jsonText: val });
            }}
            style={{
              width: '100%',
              minHeight: 300,
              fontFamily: 'monospace',
              fontSize: 12,
              padding: 8,
              borderRadius: 4,
              border: jsonError ? '1px solid #d32f2f' : '1px solid #ccc',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          {jsonError && (
            <FormHelperText error sx={{ whiteSpace: 'pre-wrap' }}>
              {jsonError}
            </FormHelperText>
          )}
        </>
      )}

      {sourceType === 'agent' && (
        <AgentDataSection
          agentId={agentAgent.agentId}
          setAgentId={agentAgent.setAgentId}
          agentIdError={agentAgent.agentIdError}
          extensionPath={agentAgent.extensionPath}
          setExtensionPath={agentAgent.setExtensionPath}
          extensionPathError={agentAgent.extensionPathError}
          fetchState={agentAgent.fetchState}
          fetchError={agentAgent.fetchError}
          validateAgentId={agentAgent.validateAgentId}
          validateExtensionPath={agentAgent.validateExtensionPath}
          handleFetchPreview={agentAgent.handleFetchPreview}
          agentDataExample={agentDataExample as Record<string, unknown> | undefined}
          agents={agentAgent.agents}
        />
      )}
    </Box>
  );

  return activeTab === 0 ? infoTab : dataTab;
}

// ─── buildDefaultWidgetConfig ─────────────────────────────────────────────────

function buildDefaultWidgetConfig(
  type: string,
  registry: ReturnType<typeof useWidgetRegistry>['registry']
): WidgetConfig {
  const entry = registry.get(type);
  const defaultCfg = entry?.defaultConfig ?? { title: 'New Widget' };
  const id = crypto.randomUUID();
  switch (type) {
    case 'chart':
      return { id, type: 'chart', config: defaultCfg as ChartWidgetConfig };
    case 'text':
      return { id, type: 'text', config: defaultCfg as TextWidgetConfig };
    case 'metric_card':
      return { id, type: 'metric_card', config: defaultCfg as MetricCardWidgetConfig };
    case 'code':
      return { id, type: 'code', config: defaultCfg as CodeWidgetConfig };
    case 'rep_dashboard':
      return { id, type: 'rep_dashboard', config: defaultCfg as RepDashboardWidgetConfig };
    default:
      return { id, type: 'chart', config: defaultCfg as ChartWidgetConfig };
  }
}

// ─── ChartConfigSidebar ───────────────────────────────────────────────────────

/**
 * ChartConfigSidebar — persistent right panel for widget configuration.
 *
 * Opens in two modes:
 * - **create-widget**: user chose a type from the palette; footer button says "Create Widget"
 * - **chart-config**: user clicked an existing widget; footer button says "Save Widget"
 *
 * A single `commitRef` is used so each form can register its "flush local state
 * back to context" callback during every render without triggering extra effects.
 */
export const ChartConfigSidebar = memo(function ChartConfigSidebar() {
  const {
    sidebarPanel,
    setSidebarPanel,
    pendingSlot,
    setPendingSlot,
    pendingWidgetType,
    setPendingWidgetType,
    selectedWidgetId,
    setSelectedWidgetId,
    showDataSource,
  } = useDashboardBuilder();
  const {
    updateWidgetConfig,
    updateWidgetDataSource,
    assignWidgetToSlot,
    addBlockWithWidget,
    autoSizeWidget,
  } = useDashboardBuilderEditing();
  const { config } = useDashboardConfig();
  const { updateWidgetData, widgetData } = useDashboardDataContext();
  const { registry } = useWidgetRegistry();

  const isOpen = sidebarPanel === 'chart-config' || sidebarPanel === 'create-widget';
  const isCreateMode = sidebarPanel === 'create-widget';

  /** Ref the active form assigns a (() => string|null) each render — returns null on success, error string on invalid */
  const commitRef = useRef<(() => string | null) | null>(null);
  /** Written synchronously by the form during commit — read by the footer handler */
  const committedRef = useRef<CommittedValues | null>(null);
  const [footerError, setFooterError] = useState<string | null>(null);

  // Active config tab: 0 = Info, 1 = Data (hidden when showDataSource is false)
  const [activeTab, setActiveTab] = useState(0);

  // Live preview state — tracks the current local form values for the preview render
  const [liveConfig, setLiveConfig] = useState<Partial<WidgetSpecificConfig>>({});
  const [liveDataSource, setLiveDataSource] = useState<LiveDataSource>({ type: 'static', data: null });

  // Resizable sidebar
  const [sidebarWidth, setSidebarWidth] = useState(420);
  const sidebarWidthRef = useRef(420);
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidthRef.current;
    const onMove = (ev: MouseEvent) => {
      const newW = Math.max(380, Math.min(600, startW - (ev.clientX - startX)));
      sidebarWidthRef.current = newW;
      setSidebarWidth(newW);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Find the widget being edited (edit mode)
  const editingWidget = selectedWidgetId
    ? (() => {
        for (const block of config.blocks) {
          for (const item of block.items) {
            if (item.type === 'widget' && item.widget.id === selectedWidgetId) return item.widget;
            if (item.type === 'section') {
              for (const bw of item.section.widgets) {
                if (bw.widget.id === selectedWidgetId) return bw.widget;
              }
            }
          }
        }
        return null;
      })()
    : null;

  // Draft widget for create mode — initialized once when the panel opens
  const [draftWidget, setDraftWidget] = useState<WidgetConfig | null>(null);

  // When create-widget panel opens, initialise the draft and reset tab+preview
  const prevPanelRef = useRef(sidebarPanel);
  if (sidebarPanel === 'create-widget' && prevPanelRef.current !== 'create-widget') {
    prevPanelRef.current = sidebarPanel;
    const type = pendingWidgetType ?? 'chart';
    const newDraft = buildDefaultWidgetConfig(type, registry);
    setDraftWidget(newDraft);
    setActiveTab(0);
    setLiveConfig(newDraft.config);
    setLiveDataSource({ type: 'static', data: null });
  } else if (sidebarPanel === 'chart-config' && prevPanelRef.current !== 'chart-config') {
    prevPanelRef.current = sidebarPanel;
    setActiveTab(0);
    if (editingWidget) {
      setLiveConfig(editingWidget.config);
      setLiveDataSource((editingWidget.dataSource ?? { type: 'static', data: null }) as LiveDataSource);
    }
  } else if (sidebarPanel !== 'create-widget' && sidebarPanel !== 'chart-config') {
    prevPanelRef.current = sidebarPanel;
  }

  const activeWidget = isCreateMode ? draftWidget : editingWidget;

  // Build a merged "live widget" used only for the preview — combines base widget + live edits
  const liveWidget = useMemo<WidgetConfig | null>(() => {
    if (!activeWidget) return null;
    return {
      ...activeWidget,
      config: { ...activeWidget.config, ...liveConfig } as WidgetConfig['config'],
      // Cast: liveDataSource.data may be null until the form has data; preview handles missing data gracefully
      dataSource: liveDataSource as WidgetDataSource,
    } as WidgetConfig;
  }, [activeWidget, liveConfig, liveDataSource]);

  const handleClose = useCallback(() => {
    setSidebarPanel('none');
    setPendingSlot(null);
    setPendingWidgetType(null);
    setSelectedWidgetId(null);
    setDraftWidget(null);
    setFooterError(null);
    setActiveTab(0);
    setLiveConfig({});
    setLiveDataSource({ type: 'static', data: null });
    commitRef.current = null;
    committedRef.current = null;
  }, [setSidebarPanel, setPendingSlot, setPendingWidgetType, setSelectedWidgetId]);

  const handleUpdateConfig = useCallback(
    (partial: Partial<WidgetConfig['config']>) => {
      if (!activeWidget) return;
      if (isCreateMode) {
        setDraftWidget(
          (prev) => (prev ? { ...prev, config: { ...prev.config, ...partial } } : prev) as WidgetConfig | null
        );
      } else {
        updateWidgetConfig(activeWidget.id, partial);
      }
    },
    [activeWidget, isCreateMode, updateWidgetConfig]
  );

  const handleUpdateDataSource = useCallback(
    (ds: WidgetDataSource) => {
      if (!activeWidget) return;
      if (isCreateMode) {
        setDraftWidget((prev) => (prev ? { ...prev, dataSource: ds } : prev));
      } else {
        updateWidgetDataSource(activeWidget.id, ds);
      }
    },
    [activeWidget, isCreateMode, updateWidgetDataSource]
  );

  const handleLiveUpdate = useCallback((cfg: Partial<WidgetSpecificConfig>, ds: LiveDataSource) => {
    setLiveConfig(cfg);
    setLiveDataSource(ds);
  }, []);

  const handleFooterClick = useCallback(() => {
    committedRef.current = null;
    setFooterError(null);
    const validationError = commitRef.current?.() ?? null;
    if (validationError) {
      setFooterError(validationError);
      return;
    }
    const committed = committedRef.current;
    if (!committed) return;

    if (isCreateMode) {
      if (!pendingSlot) return;
      const { blockId, row, slot } = pendingSlot;
      const type = draftWidget?.type ?? 'chart';

      // Compute auto-rowSpan from data before creation so the widget is sized correctly
      const createData: CanonicalWidgetData | null | undefined =
        committed.dataSource.type === 'static'
          ? (committed.dataSource.data as CanonicalWidgetData | null | undefined)
          : draftWidget
            ? (widgetData.get(draftWidget.id) ?? null)
            : null;
      const regEntry = registry.get(type);
      const autoSpan = createData ? regEntry?.autoRowSpan?.(createData) : undefined;

      // When blockId is '__new__', create a new 1×1 block with the widget
      const newWidgetId =
        blockId === '__new__'
          ? addBlockWithWidget(type, committed.config, committed.dataSource, autoSpan)
          : assignWidgetToSlot(blockId, row, slot, type, committed.config, committed.dataSource, autoSpan);
      if (committed.dataSource.type === 'static') {
        // Static: canonical data is already in dataSource.data — store it immediately
        const canonicalData = committed.dataSource.data as CanonicalWidgetData | null | undefined;
        if (canonicalData != null) {
          updateWidgetData(newWidgetId, canonicalData as CanonicalWidgetData);
        }
      } else if (committed.dataSource.type === 'agent' && draftWidget) {
        // Agent: transfer the fetched preview data from the draft ID to the
        // real widget ID so data renders immediately while fetchAll re-fetches
        // in the background.
        const previewData = widgetData.get(draftWidget.id);
        if (previewData != null) {
          updateWidgetData(newWidgetId, previewData);
        }
      }
      handleClose();
    } else {
      if (editingWidget) {
        if (committed.dataSource.type === 'static') {
          const canonicalData = committed.dataSource.data as CanonicalWidgetData | null | undefined;
          if (canonicalData != null) {
            updateWidgetData(editingWidget.id, canonicalData as CanonicalWidgetData);
            autoSizeWidget(editingWidget.id, canonicalData as CanonicalWidgetData);
          }
        } else if (committed.dataSource.type === 'agent') {
          // Agent data was stored during fetch preview — auto-size based on current data
          const agentData = widgetData.get(editingWidget.id);
          if (agentData != null) {
            autoSizeWidget(editingWidget.id, agentData);
          }
        }
      }
      handleClose();
    }
  }, [
    isCreateMode,
    pendingSlot,
    draftWidget,
    editingWidget,
    assignWidgetToSlot,
    addBlockWithWidget,
    handleClose,
    updateWidgetData,
    widgetData,
    autoSizeWidget,
    registry,
  ]);

  if (!isOpen || !activeWidget) return null;

  // ── Code widget: skip sidebar entirely, go straight to full-screen modal ──
  if (activeWidget.type === 'code') {
    return (
      <CodeWidgetFullScreen
        widget={activeWidget as WidgetConfig}
        isCreateMode={isCreateMode}
        onUpdate={handleUpdateConfig}
        onUpdateDataSource={handleUpdateDataSource}
        onClose={handleClose}
        onCommit={handleFooterClick}
        commitRef={commitRef}
        committedRef={committedRef}
        onLiveUpdate={handleLiveUpdate}
      />
    );
  }

  const widgetTypeLabel = WIDGET_TYPE_LABELS[activeWidget.type] ?? activeWidget.type;

  // Build live canonical data for the preview widget
  const previewEntry = liveWidget ? registry.get(liveWidget.type) : null;
  const liveCanonical = liveDataSource.type === 'static' ? (liveDataSource.data ?? null) : null;

  return (
    <SidebarOuter style={{ width: sidebarWidth }}>
      <DragHandle onMouseDown={handleDragStart} />
      <SidebarInner>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            minHeight: 66,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {isCreateMode ? `Create ${widgetTypeLabel} Widget` : `Edit ${widgetTypeLabel} Widget`}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v: number) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          <Tab label="Info" sx={{ minHeight: 40, py: 0 }} />
          {showDataSource && <Tab label="Data" sx={{ minHeight: 40, py: 0 }} />}
        </Tabs>

        {/* Form content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <WidgetConfigForm
            widget={activeWidget as WidgetConfig}
            onUpdate={handleUpdateConfig}
            onUpdateDataSource={handleUpdateDataSource}
            commitRef={commitRef}
            committedRef={committedRef}
            activeTab={showDataSource ? activeTab : 0}
            activePanelName={activeTab === 0 ? 'info' : 'data'}
            onLiveUpdate={handleLiveUpdate}
          />
        </Box>

        <Divider />

        {/* Live Preview */}
        {liveWidget && previewEntry && (
          <>
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, flexShrink: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Preview
              </Typography>
            </Box>
            <PreviewWrapper>
              <PreviewScaler>
                {previewEntry.render({
                  widgetId: liveWidget.id,
                  config: liveWidget,
                  data: liveCanonical,
                  width: 2,
                  height: 1,
                  isLoading: false,
                })}
              </PreviewScaler>
            </PreviewWrapper>
            <Divider />
          </>
        )}

        {/* Footer */}
        <Box sx={{ px: 2, py: 1.5, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {footerError && (
            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
              {footerError}
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Button variant="outlined" size="small" fullWidth onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" size="small" fullWidth disableElevation onClick={handleFooterClick}>
              {isCreateMode ? 'Create Widget' : 'Save Widget'}
            </Button>
          </Box>
        </Box>
      </SidebarInner>
    </SidebarOuter>
  );
});

// ─── Styled components ────────────────────────────────────────────────────────

const SidebarOuter = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  minWidth: 380,
  maxWidth: 600,
  flexShrink: 0,
  height: '100%',
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

const DragHandle = styled('div')(({ theme }) => ({
  width: 4,
  cursor: 'col-resize',
  backgroundColor: theme.palette.divider,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const SidebarInner = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',
});

const PREVIEW_SCALE = 0.7;
const PREVIEW_HEIGHT = 240;

const PreviewWrapper = styled('div')(({ theme }) => ({
  flexShrink: 0,
  height: PREVIEW_HEIGHT,
  margin: theme.spacing(0, 2, 1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
}));

// Renders the widget at (1/scale) size then scales it back down so the full
// widget fills the preview box. Flex centering on the wrapper ensures left/right
// overflow is symmetric, producing a centered result.
const PreviewScaler = styled('div')({
  width: `${(1 / PREVIEW_SCALE) * 100}%`,
  height: PREVIEW_HEIGHT / PREVIEW_SCALE,
  flexShrink: 0,
  transformOrigin: 'top center',
  transform: `scale(${PREVIEW_SCALE})`,
  pointerEvents: 'none',
});
